import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sparkles, ArrowRight, Check, ShieldCheck,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

import { Sparkle, LogoMark, CreateAccountBtn, startGoogleLogin } from "./landing/primitives";
import HeroSearch from "./landing/HeroSearch";
import PricingCard from "./landing/PricingCard";
import IntervalToggle from "./landing/IntervalToggle";
import ExampleCard from "./landing/ExampleCard";
import { HERO_CHIPS, WHAT_IT_DOES, HOW_STEPS, WHAT_YOU_GET, EXAMPLES } from "./landing/copy";
import { FALLBACK_PLANS, FALLBACK_INTERVALS, mergePlans } from "./landing/pricingConfig";

/**
 * Fetch billing plans from the backend and merge them with frontend
 * presentation extras. Falls back to `FALLBACK_PLANS` if the request
 * fails so the landing page always shows correct prices.
 */
function useLandingPlans() {
  const [plans, setPlans] = useState(FALLBACK_PLANS);
  const [intervals, setIntervals] = useState(FALLBACK_INTERVALS);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/billing/plans");
        if (!cancelled) {
          setPlans(mergePlans(data?.plans));
          if (Array.isArray(data?.intervals) && data.intervals.length) {
            setIntervals(data.intervals);
          }
        }
      } catch {
        // Fallbacks are already in state — nothing to do.
      }
    })();
    return () => { cancelled = true; };
  }, []);
  return { plans, intervals };
}

export default function Landing() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { plans, intervals } = useLandingPlans();
  const [intervalId, setIntervalId] = useState("annual");

  return (
    <div className="min-h-screen iti-bg-soft text-slate-900" data-testid="landing-page">
      {/* Nav */}
      <nav className="sticky top-0 z-40 backdrop-blur-md bg-white/80 border-b border-emerald-100/70">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <LogoMark />
          <CreateAccountBtn user={user} navigate={navigate} onLogout={logout} testId="nav-create-account-btn" />
        </div>
      </nav>

      {/* ─────────────────────────────────────────────────────
          1. HERO — clear headline, prompt box, 3 benefit chips
          ───────────────────────────────────────────────────── */}
      <section className="relative" data-testid="hero-section">
        {/* decorative sparkles */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <Sparkle className="w-4 h-4 iti-sparkle absolute left-[6%] top-24 opacity-60" />
          <Sparkle className="w-3 h-3 iti-sparkle absolute right-[8%] top-32 opacity-50" />
          <Sparkle className="w-5 h-5 iti-sparkle absolute left-[14%] bottom-8 opacity-40" />
          <Sparkle className="w-3 h-3 iti-sparkle absolute right-[14%] bottom-16 opacity-40" />
        </div>

        <div className="max-w-6xl mx-auto px-6 md:px-10 pt-14 md:pt-20 pb-20 text-center relative">
          <div className="inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.22em] text-emerald-700 uppercase iti-chip px-3 py-1.5 mb-6">
            <Sparkle className="w-3 h-3" /> AI Website Builder
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] text-slate-900">
            Build your business website <br className="hidden sm:block" />
            with <span className="font-script text-emerald-600">AI</span>
            <span className="text-emerald-600">.</span>
          </h1>
          <p className="text-base sm:text-lg text-slate-600 mt-5 max-w-2xl mx-auto">
            Describe your business idea. Interest to Income builds your website, gives you target audience data,
            and creates a 30-day action plan.
          </p>

          <div className="mt-8">
            <HeroSearch />
          </div>

          <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
            {HERO_CHIPS.map((c) => (
              <div
                key={c.label}
                className="iti-chip inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold"
                data-testid={`hero-chip-${c.label.replace(/\s+/g, "-").toLowerCase()}`}
              >
                <c.icon className="w-4 h-4 text-emerald-600" strokeWidth={2.25} />
                {c.label}
              </div>
            ))}
          </div>
        </div>

        <div className="absolute inset-x-0 bottom-0 h-24 pointer-events-none"
             style={{ background: "linear-gradient(180deg, rgba(236,253,245,0) 0%, #ECFDF5 60%, #ECFDF5 100%)" }} />
      </section>

      {/* ─────────────────────────────────────────────────────
          2. WHAT IT DOES — short bullets
          ───────────────────────────────────────────────────── */}
      <section className="iti-section-soft py-16 sm:py-20 border-t border-emerald-100/60" data-testid="what-it-does-section">
        <div className="max-w-6xl mx-auto px-6 md:px-10 text-center">
          <div className="inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.22em] text-emerald-700 uppercase">
            <span className="w-10 iti-divider-line" />
            What Interest to Income does
            <span className="w-10 iti-divider-line" />
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold mt-3 text-slate-900">
            Everything you need to <span className="font-script text-emerald-600">launch</span>
            <span className="text-emerald-600">.</span>
          </h2>
          <p className="text-slate-600 mt-3 max-w-2xl mx-auto">
            A complete AI website builder that gets your business online in minutes — not weeks.
          </p>
        </div>

        <div className="max-w-5xl mx-auto px-6 md:px-10 mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {WHAT_IT_DOES.map((it, i) => (
            <div
              key={i}
              className="iti-card p-5 flex flex-col items-start gap-3 text-left"
              data-testid={`what-it-does-card-${i}`}
            >
              <div className="w-10 h-10 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center">
                <it.icon className="w-5 h-5 text-emerald-600" strokeWidth={2.25} />
              </div>
              <p className="text-sm font-semibold text-slate-800 leading-snug">{it.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ─────────────────────────────────────────────────────
          3. HOW IT WORKS — 3 numbered steps
          ───────────────────────────────────────────────────── */}
      <section id="how" className="py-20 sm:py-24 relative" data-testid="how-it-works-section">
        <div className="max-w-6xl mx-auto px-6 md:px-10 text-center">
          <div className="inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.22em] text-emerald-700 uppercase">
            <span className="w-10 iti-divider-line" />
            How it works
            <span className="w-10 iti-divider-line" />
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold mt-3 text-slate-900">
            Three simple <span className="font-script text-emerald-600">steps</span>
            <span className="text-emerald-600">.</span>
          </h2>
          <p className="text-slate-600 mt-3 max-w-2xl mx-auto">
            Go from idea to live website without touching a line of code.
          </p>
        </div>

        <div className="max-w-6xl mx-auto px-6 md:px-10 mt-12 grid md:grid-cols-3 gap-5">
          {HOW_STEPS.map((s, i) => (
            <div
              key={i}
              className="iti-card p-7 relative"
              data-testid={`how-step-${i + 1}`}
            >
              <div className="absolute -top-3 -left-3 w-9 h-9 rounded-full bg-emerald-500 text-white font-display font-extrabold text-base flex items-center justify-center shadow-md">
                {s.n}
              </div>
              <div className="w-12 h-12 rounded-2xl bg-emerald-50 border border-emerald-100 flex items-center justify-center mb-4">
                <s.icon className="w-6 h-6 text-emerald-600" strokeWidth={2.25} />
              </div>
              <div className="font-display text-lg font-extrabold text-slate-900">{s.title}</div>
              <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{s.text}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 text-center">
          <button
            onClick={startGoogleLogin}
            data-testid="how-cta-btn"
            className="iti-btn-primary px-6 py-3 text-sm font-semibold inline-flex items-center gap-2"
          >
            Try It Free
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      {/* ─────────────────────────────────────────────────────
          4. WHAT YOU GET — 8 icon cards
          ───────────────────────────────────────────────────── */}
      <section className="iti-section-soft py-20 sm:py-24 border-y border-emerald-100/60" data-testid="what-you-get-section">
        <div className="max-w-6xl mx-auto px-6 md:px-10 text-center">
          <div className="inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.22em] text-emerald-700 uppercase">
            <span className="w-10 iti-divider-line" />
            What you get
            <span className="w-10 iti-divider-line" />
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold mt-3 text-slate-900">
            Everything you need to <br className="hidden sm:block" />
            build, publish, and <span className="font-script text-emerald-600">grow</span>
            <span className="text-emerald-600">.</span>
          </h2>
          <p className="text-slate-600 mt-3 max-w-2xl mx-auto">
            Your plan includes the core tools to build, publish, and grow your website. Optional add-ons are available when you need extra help, domains, or design support.
          </p>
        </div>

        <div className="max-w-6xl mx-auto px-6 md:px-10 mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {WHAT_YOU_GET.map((f, i) => (
            <div
              key={i}
              className="iti-card p-5 group hover:-translate-y-0.5 transition-transform duration-200"
              data-testid={`what-you-get-${i}`}
            >
              <div className="w-11 h-11 rounded-2xl bg-emerald-50 border border-emerald-100 flex items-center justify-center mb-4">
                <f.icon className="w-5 h-5 text-emerald-600" strokeWidth={2.25} />
              </div>
              <div className="font-display text-base font-extrabold text-slate-900">{f.title}</div>
              <p className="text-xs text-slate-600 mt-1 leading-relaxed">{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ─────────────────────────────────────────────────────
          5. EXAMPLE WEBSITES
          ───────────────────────────────────────────────────── */}
      <section className="py-20 sm:py-24" data-testid="examples-section">
        <div className="max-w-6xl mx-auto px-6 md:px-10 text-center">
          <div className="inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.22em] text-emerald-700 uppercase">
            <span className="w-10 iti-divider-line" />
            Example websites
            <span className="w-10 iti-divider-line" />
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold mt-3 text-slate-900">
            See what others are <span className="font-script text-emerald-600">building</span>
            <span className="text-emerald-600">.</span>
          </h2>
          <p className="text-slate-600 mt-3 max-w-2xl mx-auto">
            Real websites generated with Interest to Income — different goals, different styles, all built in minutes.
          </p>
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-10 mt-10">
          <div className="flex gap-5 overflow-x-auto pb-4 snap-x snap-mandatory" data-testid="examples-row">
            {EXAMPLES.map((ex, i) => (
              <div key={i} className="snap-start"><ExampleCard ex={ex} /></div>
            ))}
          </div>
        </div>

        <div className="mt-10 text-center">
          <button
            onClick={startGoogleLogin}
            data-testid="examples-cta-btn"
            className="iti-btn-primary px-6 py-3 text-sm font-semibold inline-flex items-center gap-2"
          >
            Build My Website
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      {/* ─────────────────────────────────────────────────────
          6. PRICING — 4 plans, driven by GET /api/billing/plans
          ───────────────────────────────────────────────────── */}
      <section id="pricing" className="iti-section-soft py-20 sm:py-24 border-t border-emerald-100/60 relative" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center">
            <div className="inline-flex items-center gap-3 text-[11px] font-bold tracking-[0.22em] text-emerald-700 uppercase">
              <span className="w-10 iti-divider-line" />
              Pricing
              <span className="w-10 iti-divider-line" />
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold mt-3 text-slate-900">
              Simple pricing. Built to <span className="font-script text-emerald-600">launch</span>
              <span className="text-emerald-600">.</span>
            </h2>
            <p className="text-slate-600 mt-3 max-w-2xl mx-auto">
              Start simple. Add more when you need it. Cancel anytime — no hidden fees.
            </p>
            <div className="mt-6 flex justify-center">
              <IntervalToggle
                intervals={intervals}
                value={intervalId}
                onChange={setIntervalId}
                testidPrefix="pricing-interval"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mt-12 items-start">
            {plans.map((p) => (
              <PricingCard
                key={p.id}
                p={p}
                intervalId={intervalId}
                onPick={(planId) => {
                  // Stash the selected plan so Onboarding can route to the
                  // pay-before-generate flow after Google sign-in. Free plan
                  // → clear any stale selection so the user goes through the
                  // normal free onboarding.
                  try {
                    if (planId && planId !== "free") {
                      localStorage.setItem("iti.selectedPlanId", planId);
                      localStorage.setItem("iti.selectedInterval", intervalId);
                    } else {
                      localStorage.removeItem("iti.selectedPlanId");
                      localStorage.removeItem("iti.selectedInterval");
                    }
                  } catch { /* ignore quota / private mode */ }
                  startGoogleLogin();
                }}
              />
            ))}
          </div>

          <div className="mt-6 text-center text-xs text-slate-500 max-w-2xl mx-auto" data-testid="pricing-disclaimer">
            Plan benefits apply per account. Hosting is included for your first website. Additional live websites
            require $50/month hosting each. Monthly plans do not include a free custom domain. Bi-Annual and Annual
            plans include one free standard domain for the first website after qualifying payment.
          </div>

          <p className="text-xs text-slate-500 mt-8 text-center inline-flex items-center gap-2 w-full justify-center">
            <Check className="w-3.5 h-3.5 text-emerald-500" strokeWidth={3} />
            Cancel anytime. No hidden fees.
          </p>
        </div>
      </section>

      {/* ─────────────────────────────────────────────────────
          7. FINAL CTA
          ───────────────────────────────────────────────────── */}
      <section className="py-20 sm:py-24" data-testid="final-cta-section">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <div className="iti-card p-10 sm:p-14 text-center relative overflow-hidden">
            <Sparkle className="w-5 h-5 iti-sparkle absolute left-8 top-8 opacity-60" />
            <Sparkle className="w-3 h-3 iti-sparkle absolute right-12 top-10 opacity-50" />
            <Sparkle className="w-4 h-4 iti-sparkle absolute left-14 bottom-10 opacity-40" />
            <Sparkle className="w-3 h-3 iti-sparkle absolute right-8 bottom-8 opacity-50" />

            <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900">
              Ready to turn your idea <br className="hidden sm:block" />
              into a <span className="font-script text-emerald-600">website</span>
              <span className="text-emerald-600">?</span>
            </h2>
            <p className="text-base sm:text-lg text-slate-600 mt-5 max-w-2xl mx-auto">
              Describe your business and let AI build the first version for you.
            </p>
            <button
              onClick={startGoogleLogin}
              data-testid="footer-cta-btn"
              className="iti-btn-primary mt-7 px-7 py-3.5 text-base font-semibold inline-flex items-center gap-2"
            >
              Start Building
              <ArrowRight className="w-4 h-4" />
            </button>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-4 text-xs text-slate-500">
              <span className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-500" strokeWidth={3} /> Free to start</span>
              <span className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-500" strokeWidth={3} /> No credit card required</span>
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-emerald-500" /> Cancel anytime</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-emerald-100/70 bg-white/70 backdrop-blur-md py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-10 flex flex-col md:flex-row items-center justify-between gap-3 text-sm text-slate-500">
          <div className="flex items-center gap-2">
            <span className="iti-sparkle"><Sparkle className="w-3.5 h-3.5" /></span>
            © Interest To Income
          </div>
          <div className="flex items-center gap-5">
            <a href="/terms" data-testid="footer-terms-link" className="hover:text-emerald-700 transition-colors">Terms &amp; Conditions</a>
            <a href="/privacy" data-testid="footer-privacy-link" className="hover:text-emerald-700 transition-colors">Privacy Policy</a>
          </div>
          <div className="hidden md:block">Built for beginners. Made to launch.</div>
        </div>
      </footer>
    </div>
  );
}
